/**
 * Video oEmbed helpers — resolves thumbnail URLs for Vimeo + TikTok
 * external videos. YouTube has predictable URLs (`i.ytimg.com/vi/<id>/
 * hqdefault.jpg`) that don't need an oEmbed call; the public-facing
 * render computes those at build time.
 *
 * All helpers are server-side only. Each fetch has a hard 3s timeout
 * via AbortController so a slow oEmbed endpoint never blocks the
 * caller indefinitely (page loaders, server actions).
 *
 * Migration `20260512120000` adds `artist_media.thumbnail_url`, which
 * `refreshVideoThumbnailsAction` populates from the values returned
 * here.
 */

const OEMBED_TIMEOUT_MS = 3_000;

export type VideoSource = 'youtube' | 'vimeo' | 'tiktok' | 'unknown';

/** Classify an external_url by hostname. */
export function classifyVideoSource(url: string | null): VideoSource {
  if (!url) return 'unknown';
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    if (host.includes('youtube') || host === 'youtu.be') return 'youtube';
    if (host.includes('vimeo')) return 'vimeo';
    if (host.includes('tiktok')) return 'tiktok';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Fetch the canonical thumbnail URL for a Vimeo or TikTok video.
 * Returns null on any failure (network, parse, timeout, non-200).
 * Callers persist non-null returns to `artist_media.thumbnail_url`
 * and treat null as "don't retry on this render — schedule a backfill
 * later instead."
 */
export async function fetchVideoThumbnail(
  externalUrl: string,
): Promise<string | null> {
  const source = classifyVideoSource(externalUrl);
  if (source === 'vimeo') return fetchVimeoThumbnail(externalUrl);
  if (source === 'tiktok') return fetchTikTokThumbnail(externalUrl);
  return null;
}

export interface VideoMeta {
  /** Human title from the provider's oEmbed response, trimmed. */
  title: string | null;
  /** Canonical thumbnail URL from oEmbed (Vimeo / TikTok / YouTube). */
  thumbnailUrl: string | null;
}

/**
 * Fetch oEmbed metadata (title + thumbnail) for a YouTube / Vimeo /
 * TikTok video. Returns nulls on any failure (network, parse, timeout,
 * non-200) so callers fall back to a user-supplied title or the
 * deterministic YouTube thumbnail. Server-side only; 3s hard timeout.
 *
 * Used by `DancerMediaService.addVideoLink` (resolve a title at add time
 * so clips never render as "Untitled clip") and one-off title backfills.
 */
export async function fetchVideoMeta(externalUrl: string): Promise<VideoMeta> {
  const endpoint = oembedEndpoint(
    classifyVideoSource(externalUrl),
    externalUrl,
  );
  if (!endpoint) return { title: null, thumbnailUrl: null };
  try {
    const json = await fetchJsonWithTimeout(endpoint);
    if (!json || typeof json !== 'object') {
      return { title: null, thumbnailUrl: null };
    }
    const obj = json as Record<string, unknown>;
    const title =
      typeof obj.title === 'string' && obj.title.trim().length > 0
        ? obj.title.trim()
        : null;
    const thumbnailUrl =
      typeof obj.thumbnail_url === 'string' && obj.thumbnail_url.length > 0
        ? obj.thumbnail_url
        : null;
    return { title, thumbnailUrl };
  } catch {
    return { title: null, thumbnailUrl: null };
  }
}

/** Public oEmbed endpoint for a source, or null if we can't resolve one. */
function oembedEndpoint(source: VideoSource, url: string): string | null {
  const u = encodeURIComponent(url);
  switch (source) {
    case 'youtube':
      return `https://www.youtube.com/oembed?url=${u}&format=json`;
    case 'vimeo':
      return `https://vimeo.com/api/oembed.json?url=${u}`;
    case 'tiktok':
      return `https://www.tiktok.com/oembed?url=${u}`;
    default:
      return null;
  }
}

/**
 * Vimeo oEmbed — public, unauthenticated, CORS-allowed.
 * Endpoint: https://vimeo.com/api/oembed.json?url=<video URL>
 * Response (subset): { thumbnail_url, thumbnail_url_with_play_button }
 */
async function fetchVimeoThumbnail(url: string): Promise<string | null> {
  try {
    const endpoint = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`;
    const json = await fetchJsonWithTimeout(endpoint);
    if (!json || typeof json !== 'object') return null;
    const thumb = (json as Record<string, unknown>).thumbnail_url;
    return typeof thumb === 'string' && thumb.length > 0 ? thumb : null;
  } catch {
    return null;
  }
}

/**
 * TikTok oEmbed — public, unauthenticated.
 * Endpoint: https://www.tiktok.com/oembed?url=<video URL>
 * Response (subset): { thumbnail_url }
 */
async function fetchTikTokThumbnail(url: string): Promise<string | null> {
  try {
    const endpoint = `https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`;
    const json = await fetchJsonWithTimeout(endpoint);
    if (!json || typeof json !== 'object') return null;
    const thumb = (json as Record<string, unknown>).thumbnail_url;
    return typeof thumb === 'string' && thumb.length > 0 ? thumb : null;
  } catch {
    return null;
  }
}

async function fetchJsonWithTimeout(endpoint: string): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), OEMBED_TIMEOUT_MS);
  try {
    const res = await fetch(endpoint, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
      // Don't follow third-party redirects more than once.
      redirect: 'follow',
    });
    if (!res.ok) return null;
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}
